import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, AlignLeft, ArrowLeft, Calendar, CheckCircle2, ExternalLink, Pause, Play, RefreshCw, Repeat, Trash2, Undo2, Wrench, XCircle } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ActionMenu } from '../components/ui/ActionMenu'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { Timestamp } from '../components/ui/Timestamp'
import { LifeAreasPicker } from '../components/ui/LifeAreasPicker'
import { useToast } from '../components/ui/Toast'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { useGoalReviewPrompts } from '../hooks/useGoalReviewPrompts'
import { GoalProgressReviewCard } from '../components/automom/GoalProgressReviewCard'
import { GoalService, type Goal, type GoalBoardCard, type GoalJourneyPlan } from '../services/goalService'
import { GoalGapsCard } from '../components/goals/GoalGapsCard'
import { GoalKeepingUp } from '../components/goals/GoalKeepingUp'
import { GoalHoldMeToIt } from '../components/goals/GoalHoldMeToIt'
import { GoalJourney } from '../components/goals/GoalJourney'
import { GoalAddRow, GoalDoneMeansEditor, GoalParentEditor, GoalPriorityEditor, GoalTargetDateEditor, GoalTitleEditor, GoalWhyEditor } from '../components/goals/inline/GoalInlineEditors'
import { nextTitleWords, nextWhenWords, priorityWord, progressWords } from '../utils/goalWords'
import { SECTION_TITLE, goalState, sectionOf, targetPassed } from '../utils/goalSections'
import { describeTaskCleanup } from '../utils/taskForm'
import DreamGraphTabs from '../components/hierarchy/DreamGraphTabs'
import BlockersSection from '../components/blockers/BlockersSection'
import { OccurrencesSection } from '../components/occurrences/OccurrencesSection'

/**
 * The goal page (Goals UX pass 2026-09-06), in the task page's frame:
 * breadcrumb → title with one summary line → the verbs → body | rail. Every
 * fact is edited where it is shown; the Add row offers what is not set yet;
 * Mark done has an Undo; pause, resume and let go are verbs. The rail carries
 * the canonical order — Fill the gaps · Plan · Hold me to it · Keeping up.
 *
 * A goal is a container: its journey is tasks, skills and habits, and the
 * page reads them. Nothing is scheduled, logged or reminded on the goal.
 */
const SECTION_HEADING = 'flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

export const GoalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const { timezone } = useUserTimezone()
  const [searchParams, setSearchParams] = useSearchParams()
  const [goal, setGoal] = useState<Goal | null>(null)
  const [card, setCard] = useState<GoalBoardCard | null>(null)
  const [plan, setPlan] = useState<GoalJourneyPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [letGoOpen, setLetGoOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [dateOpen, setDateOpen] = useState(0)
  const goalService = useMemo(() => new GoalService(), [])
  const goalNumericId = id ? parseInt(id, 10) : NaN
  const reviews = useGoalReviewPrompts()
  const review = reviews.prompts.find((p) => Number(p.goal_id) === goalNumericId) || null
  const autoStart = searchParams.get('journey') === 'start' || searchParams.get('action') === 'breakdown'

  useProvideFeedbackContext({ pageType: 'goal_detail', entityType: 'goal', entityId: id, extra: { title: goal?.title, status: goal?.status } })

  useEffect(() => {
    if (!searchParams.get('action')) return
    const next = new URLSearchParams(searchParams); next.delete('action'); setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadCard = useCallback(async () => {
    if (!id) return
    try {
      const board = await goalService.getBoard(todayKey())
      setCard(board.goals[String(id)] || null)
    } catch { setCard(null) }
  }, [goalService, id])

  const load = useCallback(async () => {
    if (!id) return
    try {
      setError(null)
      const data = await goalService.getGoal(id)
      setGoal(data)
      void loadCard()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load goal')
    } finally {
      setLoading(false)
    }
  }, [goalService, id, loadCard])

  useEffect(() => { void load() }, [load])

  const onChanged = (g: Goal) => { setGoal(g); void loadCard() }
  const onPlan = useCallback((p: GoalJourneyPlan | null) => { setPlan(p); void loadCard() }, [loadCard])

  // ── verbs ──
  const setStatus = async (status: 'completed' | 'active' | 'paused_by_user' | 'cancelled', words: { title: string; description?: string; undo?: boolean }, restore?: { status: string; progress: number }) => {
    if (!goal) return
    setBusy(status)
    try {
      const before = { status: goal.status, progress: goal.progress }
      await goalService.updateGoal(goal.id, restore ? { status: restore.status, progress: restore.progress } : status === 'completed' ? { status, progress: 100 } : { status })
      onChanged(await goalService.getGoal(goal.id))
      setLetGoOpen(false)
      toast.toast({ variant: status === 'completed' ? 'success' : 'info', title: words.title, description: words.description,
                    action: words.undo ? { label: 'Undo', onClick: () => void setStatus('active', { title: 'Back as it was' }, { status: before.status || 'active', progress: before.progress }) } : undefined })
    } catch (err) {
      toast.error('That did not go through', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const confirmDelete = async () => {
    if (!goal) return
    try {
      const result = await goalService.deleteGoal(goal.id)
      setDeleteOpen(false)
      toast.toast({ variant: 'success', title: 'Goal deleted', description: describeTaskCleanup(result.cleanup) || 'What sat under it stays, on its own.' })
      navigate('/goals')
    } catch (err) {
      setDeleteOpen(false)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  // ── frame ──
  if (loading) {
    return <div className="flex h-64 items-center justify-center"><RefreshCw className="h-6 w-6 animate-spin text-brand" /><span className="ml-3 text-secondary">Loading goal…</span></div>
  }
  if (error || !goal) {
    return (
      <div className="space-y-4">
        <Link to="/goals" className="inline-flex items-center gap-1 text-app-meta text-muted hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Goals</Link>
        <Card className="border-bad/30"><CardContent className="pt-6"><div className="py-8 text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">{error || 'This goal is not here'}</h2>
          <Button onClick={() => navigate('/goals')}>Go to goals</Button>
        </div></CardContent></Card>
      </div>
    )
  }

  const state = goalState(goal)
  const finished = state.key === 'done' || state.key === 'let_go'
  const resting = state.key === 'paused'
  const live = !finished && !resting
  const stateDot = state.key === 'done' ? 'bg-ok' : finished || resting ? 'bg-muted' : card?.today ? 'bg-ok' : 'bg-brand'
  const passed = live && targetPassed(goal, todayKey())
  const nextWhen = nextWhenWords(card?.next, timezone)
  const parentHref = goal.parent ? `/${goal.parent.type}s/${goal.parent.id}` : null
  // The state word is the list's section word (North Star rule 2, 2026-09-12):
  // the page said "Open" over a row filed under "Needs a journey".
  const stateWords = state.words || (card?.today ? 'Something done today' : SECTION_TITLE[sectionOf(goal, card, todayKey())])
  // One filled button (rule 3): the next thing while live; Mark done once the
  // journey is complete; Plan the journey when there is none; Resume while
  // paused; the review banner's "It is done" when the date has passed;
  // nothing else while the persona's review card waits.
  const journeyComplete = (plan && plan.status === 'completed') || card?.path?.status === 'completed'
  const quiet = passed || Boolean(review)
  const doneLeads = live && Boolean(journeyComplete) && !quiet
  const nextLeads = live && Boolean(card?.next) && !journeyComplete && !quiet
  const planLeads = live && !card?.next && !plan && !journeyComplete && !quiet

  type Fact = { key: string; label: string; value: React.ReactNode }
  const facts: Fact[] = []
  if (goal.success_criteria) facts.push({ key: 'done', label: 'Done means', value: live ? <GoalDoneMeansEditor goal={goal} onChanged={onChanged} /> : goal.success_criteria })
  if (goal.parent?.title) facts.push({ key: 'parent', label: 'Belongs to', value: <span className="inline-flex items-center gap-1">{parentHref ? <Link to={parentHref} className="hover:text-brand hover:underline">{goal.parent.title}</Link> : goal.parent.title}{live && goal.parent.type === 'dream' && <GoalParentEditor goal={goal} onChanged={onChanged} variant="icon" trigger={<span aria-label="Change" className="text-app-micro">✎</span>} />}</span> })
  if (goal.priority === 'high' || goal.priority === 'critical') facts.push({ key: 'priority', label: 'Priority', value: live ? <GoalPriorityEditor goal={goal} onChanged={onChanged} /> : priorityWord(goal.priority) })

  const menuItems = [
    ...(live ? [{ label: 'Let it go for now', icon: <XCircle className="h-4 w-4" />, onSelect: () => setLetGoOpen(true), testId: 'goal-let-go' }] : []),
    ...(finished ? [{ label: 'Reopen', icon: <Undo2 className="h-4 w-4" />, onSelect: () => void setStatus('active', { title: 'Back on', description: 'Pick up where you left off.' }), testId: 'goal-reopen' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => setDeleteOpen(true), danger: true, testId: 'goal-delete' },
  ]

  return (
    <div className="space-y-4">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-app-meta text-muted" data-testid="goal-breadcrumb">
        <Link to="/goals" className="inline-flex shrink-0 items-center gap-1 hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Goals</Link>
        {goal.parent?.title && parentHref && (<><span aria-hidden="true">/</span><Link to={parentHref} className="truncate hover:text-brand" data-testid="goal-parent-link">{goal.parent.title}</Link></>)}
      </nav>

      {review && (
        <GoalProgressReviewCard prompt={review} markShown={reviews.markShown} respond={reviews.respond} onDone={() => { void reviews.refetch(); void load() }} />
      )}

      {passed && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand-subtle p-3" role="status" data-testid="goal-target-review">
          <span className="text-app-meta text-primary">The date you set for this has passed. How is it going?</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="xs" onClick={() => void setStatus('completed', { title: 'Done', description: goal.title, undo: true })} data-testid="goal-review-done"><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />It is done</Button>
            <Button size="xs" variant="outline" onClick={() => setDateOpen((n) => n + 1)} data-testid="goal-review-new-date">Still going · pick a new date</Button>
            <Button size="xs" variant="outline" onClick={() => setLetGoOpen(true)} data-testid="goal-review-let-go">Let it go for now</Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <CardTitle className={`text-xl leading-tight sm:text-2xl ${finished ? 'text-secondary' : 'text-primary'}`}>
              {live ? <GoalTitleEditor goal={goal} onChanged={onChanged} trigger={goal.title} /> : goal.title}
            </CardTitle>
            <div className="inline-flex flex-wrap items-center gap-x-1.5 text-app-meta text-secondary" data-testid="goal-summary-line">
              <span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${stateDot}`} aria-hidden="true" />{stateWords}</span>
              {!finished && progressWords(goal.progress) && <span>· {progressWords(goal.progress)}</span>}
              <span>· <LifeAreasPicker value={(goal.life_areas && goal.life_areas.length ? goal.life_areas : goal.life_area ? [goal.life_area] : [])} editable={live}
                                       onSave={async (values) => { await goalService.updateGoal(goal.id, { life_areas: values }); onChanged(await goalService.getGoal(goal.id)) }} testId="goal-life-area" /></span>
              {goal.target_date && <span>· {live ? <GoalTargetDateEditor goal={goal} onChanged={onChanged} openRequest={dateOpen} /> : <>by <Timestamp value={`${goal.target_date.slice(0, 10)}T12:00:00`} mode="date" /></>}</span>}
              {state.key === 'done' && goal.completed_at && <span className="text-ok">· done <Timestamp value={goal.completed_at} mode="datetime" /></span>}
              {live && card?.today && <span className="text-ok">· {card.today.kind === 'habit' ? 'habit done' : card.today.kind === 'skill' ? 'skill practised' : 'task done'} {card.today.at ? <Timestamp value={card.today.at} mode="time" /> : 'today'}</span>}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="goal-actions">
            {live && card?.next && (
              <Button variant={nextLeads ? 'default' : 'outline'} size="xs" className={nextLeads ? '' : 'hover:bg-brand-subtle'} onClick={() => navigate(`/${card.next!.kind}s/${card.next!.id}`)} data-testid="goal-next-step">
                {card.next.kind === 'habit' ? <Repeat className="mr-2 h-4 w-4" /> : card.next.kind === 'skill' ? <Wrench className="mr-2 h-4 w-4" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                {card.next.kind === 'habit' ? 'Next: the habit' : card.next.kind === 'skill' ? 'Next: the skill' : 'Next step'}
              </Button>
            )}
            {live && (
              <Button variant={doneLeads ? 'default' : 'outline'} size="xs" className={doneLeads ? '' : 'hover:bg-ok/10'} disabled={busy === 'completed'} onClick={() => void setStatus('completed', { title: 'Done', description: goal.title, undo: true })} data-testid="goal-done">
                <CheckCircle2 className="mr-2 h-4 w-4" />Mark done
              </Button>
            )}
            {live && (
              <Button variant="outline" size="xs" className="hover:bg-brand-subtle" disabled={busy === 'paused_by_user'} onClick={() => void setStatus('paused_by_user', { title: 'Paused', description: 'It waits here until you pick it back up. Nothing will nag you meanwhile.' })} data-testid="goal-pause">
                <Pause className="mr-2 h-4 w-4" />Pause
              </Button>
            )}
            {resting && (
              <Button size="xs" disabled={busy === 'active'} onClick={() => void setStatus('active', { title: 'Back on', description: 'Pick up where you left off.' })} data-testid="goal-resume">
                <Play className="mr-2 h-4 w-4" />Resume
              </Button>
            )}
            {finished && (
              <Button variant="outline" size="xs" className="hover:bg-brand-subtle" disabled={busy === 'active'} onClick={() => void setStatus('active', { title: 'Back on', description: 'Pick up where you left off.' })} data-testid="goal-reopen-button">
                <Undo2 className="mr-2 h-4 w-4" />Reopen
              </Button>
            )}
            <ActionMenu label={`More for ${goal.title}`} items={menuItems} testId="goal-more" />
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid gap-6 border-t border-soft pt-4 lg:grid-cols-5">
            <div className="min-w-0 space-y-5 lg:col-span-3" data-testid="goal-body">
              <section data-testid="goal-about">
                <h3 className={SECTION_HEADING}><AlignLeft className="h-3.5 w-3.5 text-brand" /> About</h3>
                <p className="mt-2 text-sm text-primary" data-testid="goal-why">
                  {goal.description ? (live ? <GoalWhyEditor goal={goal} onChanged={onChanged} trigger={goal.description} /> : goal.description) : <span className="text-muted">Nothing written yet{live ? ' — the Add row below takes it' : ''}.</span>}
                </p>
                {facts.length > 0 && (
                  <dl className="mt-3 text-sm" data-testid="goal-facts">
                    {facts.map((f) => (
                      <div key={f.key} className="flex gap-3 py-1" data-testid={`goal-fact-${f.key}`}>
                        <dt className="w-28 shrink-0 pt-0.5 text-app-meta text-muted">{f.label}</dt>
                        <dd className="min-w-0 break-words text-primary">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {live && <div className="mt-3"><GoalAddRow goal={goal} onChanged={onChanged} /></div>}
              </section>

              <GoalJourney goal={goal} card={card} live={live} headingClass={SECTION_HEADING} tz={timezone} onPlan={onPlan} refreshKey={`${card?.done_tasks ?? ''}:${card?.open_tasks ?? ''}`} autoStart={autoStart} leads={planLeads} />

              {/* The Progress panel is gone (the plan's cut list, 2026-09-06): the
                  percentage lives in Keeping up, where it is edited. The log is just
                  the log: Keeping up owns the count, the strip and the last time. */}
              <OccurrencesSection itemType="goal" itemId={goalNumericId} refreshKey={`${goal.updated_at || ''}:${card?.today?.id || ''}:${card?.days_total ?? ''}`} headingClass={SECTION_HEADING} logOnly />

              {!Number.isNaN(goalNumericId) && <BlockersSection itemType="goal" itemId={goalNumericId} className="rounded-xl border border-soft bg-surface-alt p-4" />}
            </div>

            <aside className="min-w-0 space-y-4 rounded-xl bg-surface-alt p-4 lg:col-span-2" data-testid="goal-rail">
              {live && <GoalGapsCard goal={goal} onChanged={onChanged} embedded />}

              <div className={live ? 'border-t border-soft pt-4' : ''} data-testid="goal-plan">
                <h3 className={SECTION_HEADING}><Calendar className="h-3.5 w-3.5 text-brand" /> Plan</h3>
                <div className="mt-1.5 space-y-1 text-sm text-primary">
                  <div data-testid="goal-plan-next">
                    {card?.next ? (
                      <span>Next: <Link to={`/${card.next.kind}s/${card.next.id}`} className="font-medium hover:text-brand hover:underline">{nextTitleWords(card.next)}</Link>{nextWhen ? <span className="text-secondary"> · {nextWhen}</span> : <span className="text-secondary"> · not on the calendar yet</span>}</span>
                    ) : plan && plan.status === 'completed' ? (
                      <span className="text-secondary">The journey is complete. Mark the goal done, or plan a new one.</span>
                    ) : (
                      <span className="text-secondary">{live ? 'No next step yet · plan the journey below' : 'Nothing planned'}</span>
                    )}
                  </div>
                  {/* Plan holds what is set; an unset by-when is offered once, in the Add
                      row and the gaps card. The editor stays mounted while a review
                      banner may open it. */}
                  {(goal.target_date || (live && passed)) && (
                    <div data-testid="goal-plan-by">
                      {live
                        ? <GoalTargetDateEditor goal={goal} onChanged={onChanged} openRequest={dateOpen} trigger={goal.target_date ? <>Get there by <Timestamp value={`${goal.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : 'Pick a new date'} />
                        : (goal.target_date ? <>Get there by <Timestamp value={`${goal.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : null)}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-soft pt-4">
                <GoalHoldMeToIt card={card} headingClass={SECTION_HEADING} />
              </div>

              <div className="border-t border-soft pt-4">
                <GoalKeepingUp goal={goal} card={card} headingClass={SECTION_HEADING} live={live} onChanged={onChanged} />
              </div>
              <p className="text-app-micro text-muted">Added <Timestamp value={goal.created_at || ''} mode="date" />{goal.updated_at ? <> · changed <Timestamp value={goal.updated_at} mode="datetime" /></> : null}</p>
            </aside>
          </div>
        </CardContent>
      </Card>

      {goal?.id && <DreamGraphTabs nodeId={Number(goal.id)} nodeType="goal" />}

      <ConfirmationModal isOpen={letGoOpen} onClose={() => setLetGoOpen(false)} onConfirm={() => void setStatus('cancelled', { title: 'Let go for now', description: 'It stays in Done, and you can reopen it any time.' })} title="Let this goal go for now?"
                         message={`“${goal.title}” moves to Done as let go. Nothing under it is removed, and you can reopen it whenever it matters again.`} variant="warning" confirmText="Let it go" />
      <ConfirmationModal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={confirmDelete} title="Delete this goal?"
                         message={`“${goal.title}” will be removed, along with the journey steps not yet started. The tasks, habits and skills under it stay, on their own.`} variant="error" confirmText="Delete" />
    </div>
  )
}

export default GoalDetail
